import { useEffect, useState } from 'react';
import templates from '@ccs/resume-templates/manifest';
import './styles.css';

type TemplateEntry = {
  id: string;
  name: string;
  summary: string;
  layout: 'tall' | 'wide' | 'short';
  sampleName: string;
  sampleGrade: string;
  sampleDirection: string;
  highlights: string[];
  index: string;
};

type MemberCard = {
  slug: string;
  isCurrent: boolean;
  meta: {
    name: string;
    grade: string;
    program: string;
    direction: string;
    summary: string;
    thumb: string | null;
  };
};

const list = templates as TemplateEntry[];

function initial(name: string): string {
  return name.trim().slice(0, 1) || '同';
}

export default function ResumeList() {
  const [members, setMembers] = useState<MemberCard[]>([]);

  useEffect(() => {
    const previous = document.title;
    document.title = '成员与简历 · CEUS';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch('/api/public/resumes')
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { resumes?: MemberCard[] } | null) => {
        if (!alive || !Array.isArray(data?.resumes)) return;
        setMembers(data.resumes.filter((item) => item.meta?.name));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="resume-page">
      <header className="resume-topbar">
        <a className="resume-brand" href="/">
          碳捕集与能源利用数智实验室
        </a>
        <nav className="resume-links" aria-label="简历导航">
          <a href="/">← 返回门户</a>
          <a href="/resume" aria-current="page">
            全部
          </a>
        </nav>
      </header>

      <main className="resume-main">
        <div className="resume-eyebrow">People / 成员</div>
        <h1>
          把课题组的人，放到<span className="resume-keep">一张</span>可以分享的页面上。
        </h1>
        <p className="resume-lede">
          课题组成员简历，点开就是可分享的个人页；下面也放了三套模板，方便新成员选择版式。
        </p>

        {members.length > 0 ? (
          <section className="resume-members" aria-label="成员简历">
            <div className="resume-section-head">
              <h2>成员</h2>
              <span>{members.filter((item) => item.isCurrent).length} 位在读</span>
            </div>
            <div className="resume-member-grid">
              {members.map((member) => (
                <a className="resume-member" href={`/resume/${member.slug}/`} key={member.slug}>
                  {member.meta.thumb ? (
                    <img
                      src={`/resume/${member.slug}/${member.meta.thumb}`}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <span className="resume-member-avatar" aria-hidden="true">
                      {initial(member.meta.name)}
                    </span>
                  )}
                  <div className="resume-member-body">
                    <h3>
                      {member.meta.name}
                      {member.isCurrent ? null : <span className="resume-alumni">已毕业</span>}
                    </h3>
                    <p className="resume-meta">
                      {member.meta.grade} · {member.meta.direction}
                    </p>
                    <p className="resume-member-summary">{member.meta.summary}</p>
                  </div>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        <section className="resume-templates" aria-label="简历模板示例">
          <div className="resume-section-head">
            <h2>模板示例</h2>
            <span>不是真实成员</span>
          </div>
          <p className="resume-note">
            <b>示例</b> 以下三套是模板，照片和姓名均为占位。
          </p>
          {list.length === 0 ? (
            <div className="resume-empty" role="status">
              <h2>模板正在准备中</h2>
              <p>稍后回来看看，或先返回门户。</p>
              <a className="resume-view" href="/">
                返回门户 →
              </a>
            </div>
          ) : (
            <div className="resume-shelf">
              {list.map((template) => (
                <article className={`resume-plate layout-${template.layout}`} key={template.id}>
                  <span className="resume-ribbon">示例 · {template.name}</span>
                  <div className="resume-plate-head">
                    <span className="resume-avatar" aria-hidden="true">
                      {initial(template.sampleName)}
                    </span>
                    <div>
                      <h2>{template.sampleName}</h2>
                      <p className="resume-meta">
                        {template.sampleGrade} · {template.sampleDirection}
                      </p>
                    </div>
                  </div>
                  <p className="resume-summary">{template.summary}</p>
                  <ul className="resume-highlights" aria-label={`${template.name}包含`}>
                    {template.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="resume-view" href={`/resume/_templates/${template.index}`}>
                    查看模板 →
                  </a>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="resume-footer">
        <span>碳捕集与能源利用数智实验室</span>
        <span>成员页由本人维护 · 沪ICP备19044375号-1</span>
      </footer>
    </div>
  );
}